import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GraphsAndStats from './GraphsAndStats';
import { useDesignTheme } from '../design/DesignThemeContext';

const SPEEDS_PAGE = 'speeds';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Overpass:wght@500;700;800&family=Overpass+Mono:wght@500;700&family=Source+Sans+3:wght@400;600&display=swap';

let surveyHtmlPromise;

function loadSurveyHtml() {
  if (!surveyHtmlPromise) {
    surveyHtmlPromise = fetch('/murray-road-speeds.html').then(response => {
      if (!response.ok) {
        throw new Error('Could not load the Murray Road speed survey');
      }
      return response.text();
    });
  }
  return surveyHtmlPromise;
}

function ensureSurveyAssets(cssText) {
  if (!document.querySelector('link[data-mrs-fonts]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    link.setAttribute('data-mrs-fonts', '');
    document.head.appendChild(link);
  }

  let style = document.querySelector('style[data-mrs]');
  if (!style) {
    style = document.createElement('style');
    style.setAttribute('data-mrs', '');
    document.head.appendChild(style);
  }
  style.textContent = cssText;
}

function MurrayRoadSpeedsSheet() {
  const hostRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const host = hostRef.current;
    let cancelled = false;
    let scriptEl = null;

    loadSurveyHtml()
      .then(html => {
        if (cancelled || !host) {
          return;
        }
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        ensureSurveyAssets(parsed.querySelector('style')?.textContent || '');

        const sheet = document.createElement('div');
        sheet.className = 'mrs';
        const wrap = parsed.querySelector('.wrap');
        const tip = parsed.querySelector('.tip');
        if (wrap) {
          sheet.appendChild(document.importNode(wrap, true));
        }
        if (tip) {
          sheet.appendChild(document.importNode(tip, true));
        }
        host.replaceChildren(sheet);

        const code = parsed.querySelector('script')?.textContent || '';
        // Inline script, not eval. The site CSP allows unsafe-inline and rejects unsafe-eval.
        scriptEl = document.createElement('script');
        scriptEl.dataset.mrsScript = '';
        scriptEl.textContent = `(() => {\n${code}\n})();`;
        document.body.appendChild(scriptEl);
      })
      .catch(loadError => {
        if (!cancelled) {
          setError(loadError.message || 'Could not load the survey');
        }
      });

    return () => {
      cancelled = true;
      scriptEl?.remove();
      if (typeof globalThis.__mrsDisconnect === 'function') {
        globalThis.__mrsDisconnect();
        globalThis.__mrsDisconnect = undefined;
      }
      if (host) {
        host.replaceChildren();
      }
    };
  }, []);

  if (error) {
    return <p className="px-4 py-6 text-gray-700">{error}</p>;
  }

  return <div ref={hostRef} data-testid="murray-road-speeds-sheet" />;
}

export default function StatisticsView({ reports, user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { copy } = useDesignTheme();
  const page = searchParams.get('stats') === SPEEDS_PAGE ? SPEEDS_PAGE : 'reports';

  const selectPage = nextPage => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'statistics');
    if (nextPage === SPEEDS_PAGE) {
      next.set('stats', SPEEDS_PAGE);
    } else {
      next.delete('stats');
    }
    setSearchParams(next);
  };

  const backToMap = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    next.delete('stats');
    setSearchParams(next);
  };

  const tabClass = selected =>
    `px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
      selected
        ? 'bg-primary-600 text-white'
        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
    }`;

  const speedsPage = page === SPEEDS_PAGE;

  return (
    <div className={speedsPage ? 'bg-[#eceff2]' : 'flex h-full min-h-0 flex-col'}>
      <div
        className={`flex flex-wrap items-center gap-2 ${speedsPage ? 'px-4 py-3' : 'mb-4'}`}
        role="tablist"
        aria-label="Statistics pages"
      >
        {speedsPage && !user && (
          <button
            type="button"
            onClick={backToMap}
            className="mr-1 px-3 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
            data-testid="speeds-back-to-map"
          >
            ← Back to map
          </button>
        )}
        <button
          type="button"
          role="tab"
          id="statistics-tab-reports"
          aria-selected={page === 'reports'}
          aria-controls="statistics-panel"
          data-testid="statistics-page-reports"
          onClick={() => selectPage('reports')}
          className={tabClass(page === 'reports')}
        >
          {copy.statisticsTitle}
        </button>
        <button
          type="button"
          role="tab"
          id="statistics-tab-speeds"
          aria-selected={page === SPEEDS_PAGE}
          aria-controls="statistics-panel"
          data-testid="statistics-page-speeds"
          onClick={() => selectPage(SPEEDS_PAGE)}
          className={tabClass(page === SPEEDS_PAGE)}
        >
          Murray Road speeds
        </button>
      </div>

      <div
        id="statistics-panel"
        role="tabpanel"
        aria-labelledby={speedsPage ? 'statistics-tab-speeds' : 'statistics-tab-reports'}
        className={speedsPage ? undefined : 'min-h-0 flex-1'}
      >
        {speedsPage ? <MurrayRoadSpeedsSheet /> : <GraphsAndStats reports={reports} user={user} />}
      </div>
    </div>
  );
}
